import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { z } from "zod";

import { sanitizeInput } from "@/helpers/helpers";
import { Accordion, AccordionContent, AccordionItem, AccordionItemNumber, AccordionTrigger } from "@/newComponents/accordion/Accordion";
import { Button } from "@/newComponents/button/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/newComponents/dialog/Dialog";
import { IconCreate, IconTick } from "@/newComponents/icon/Icon";
import { ScrollArea } from "@/newComponents/scrollArea/ScrollArea";
import Text from "@/newComponents/typography/Text";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SongShareData } from "./DetailsAndSharesStep";

import { useSongOperations } from "../../hooks/useSongOperations";
import { useAddAlbumWizardStore } from "../store/addAlbumWizardStore";
import useAlbumStore from "../store/albumStore";

// Zod schema for song form validation (imported from previous step)
const songFormSchema = z.object({
  songTitle: z
    .string()
    .min(1, "Song title is required")
    // .max(5, "Song title must be less than 5 characters"),
    .max(200, "Song title must be less than 200 characters"),
  durationMins: z
    .number()
    .min(0, "Duration minutes cannot be negative")
    .max(60, "Duration minutes must be less than 60"),
  durationSecs: z
    .number()
    .min(0, "Duration seconds cannot be negative")
    .max(59, "Duration seconds must be less than 60"),
  alternativeTitles: z.array(z.string()),
  performingArtists: z
    .array(z.string().min(1, "Performing artist name is required").max(100, "Artist name must be less than 100 characters"))
    .min(1, "At least one performing artist is required"),
  writers: z
    .array(z.object({
      writercode: z.string(),
      writername: z.string(),
      writershare: z.number().min(0).max(100),
      writerdesignationcode: z.number().min(1).max(3),
      righttocollect: z.boolean(),
    }))
    .min(1, "At least one writer is required")
    .refine((writers) => {
      const totalShares = writers.reduce((sum, writer) => sum + writer.writershare, 0);
      return Math.abs(totalShares - 100) < 0.01;
    }, "Total writer shares must equal 100%")
    .refine((writers) => {
      return writers.some(writer => writer.righttocollect === true);
    }, "At least one writer must have 'Right To Collect' enabled"),
});

// Validation progress tracking
type ValidationProgress = {
  isValidating: boolean;
  isUploading: boolean;
  isSuccess: boolean;
  currentSongIndex: number;
  totalSongs: number;
  uploadedSongs: number;
  validationErrors: Record<string, string[]>;
  hasValidationErrors: boolean;
};

export type SummaryAndSubmitStepProps = {
  onSubmit?: (songShares: Record<string, SongShareData>) => Promise<void>;
  onValidationComplete?: (isValid: boolean, submitFn?: () => Promise<void>) => void;
};

// Writer designation codes mapping
const DESIGNATION_OPTIONS = [
  { value: 1, label: "Author" },
  { value: 2, label: "Composer" },
  { value: 3, label: "Composer/Author" },
];

export default function SummaryAndSubmitStep({ onSubmit, onValidationComplete }: SummaryAndSubmitStepProps) {
  const { t } = useTranslation("album");
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;

  const { tracks: selectedTracks, albumData, songShares, setCurrentSongIndex } = useAlbumStore();
  const { previousStep } = useAddAlbumWizardStore();

  // Handler to edit a specific song
  const handleEditSong = (songId: string) => {
    // Find the index of the song in the songDataArray
    const songDataArray = Object.values(songShares);
    const songIndex = songDataArray.findIndex(song => song.songId === songId);

    if (songIndex !== -1) {
      // Set the current song index in the store
      setCurrentSongIndex(songIndex);
      // Navigate back to the previous step (DetailsAndSharesStep)
      previousStep();
    }
  };

  // Helper function to check if song title has invalid characters
  const getTitleValidationInfo = (title: string) => {
    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      hasInvalidChars,
      suggestedTitle: safeSanitized || (t("no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  // Helper function to validate alternative titles
  const validateAlternativeTitle = (title: string) => {
    if (title.length === 0)
      return { isValid: true, suggestedTitle: "" };

    const sanitized = sanitizeInput(title);
    // Handle cases where sanitizeInput returns undefined, null, or empty string
    const safeSanitized = sanitized || "";
    const hasInvalidChars = safeSanitized !== title;

    return {
      isValid: !hasInvalidChars,
      suggestedTitle: safeSanitized.length > 0 ? safeSanitized : (t("no-valid-characters-found") ?? "(no valid characters found)"),
    };
  };

  const { createSong } = useSongOperations({
    selectedAccount,
    onValidationError: (messages) => {
      console.error("Song validation error:", messages);
      toast.error(Array.isArray(messages) ? messages.join(", ") : messages);
    },
    onError: (message) => {
      console.error("Song creation error:", message);
      toast.error(message);
    },
  });

  // Validation progress state
  const [validationProgress, setValidationProgress] = useState<ValidationProgress>({
    isValidating: false,
    isUploading: false,
    isSuccess: false,
    currentSongIndex: 0,
    totalSongs: 0,
    uploadedSongs: 0,
    validationErrors: {},
    hasValidationErrors: false,
  });

  // Get validation status for all songs
  const allSongsValid = React.useMemo(() => {
    return Object.values(songShares).every((songData) => {
      if (songData == null || songData.writers.length === 0)
        return false;
      const total = songData.writers.reduce((sum, writer) => sum + writer.writershare, 0);
      const hasRTC = songData.writers.some(writer => writer.righttocollect);
      return Math.abs(total - 100) < 0.01 && hasRTC;
    });
  }, [songShares]);

  // Validate all songs before submission
  const validateAllSongs = React.useCallback(async (): Promise<boolean> => {
    setValidationProgress({
      isValidating: true,
      isUploading: false,
      isSuccess: false,
      currentSongIndex: 0,
      totalSongs: Object.keys(songShares).length,
      uploadedSongs: 0,
      validationErrors: {},
      hasValidationErrors: false,
    });

    const errors: Record<string, string[]> = {};
    let songIndex = 0;

    for (const [songId, songData] of Object.entries(songShares)) {
      setValidationProgress(prev => ({
        ...prev,
        currentSongIndex: songIndex,
      }));

      // Simulate validation delay
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        songFormSchema.parse(songData);
      }
      catch (validationError) {
        if (validationError instanceof z.ZodError) {
          errors[songId] = validationError.errors.map(err => err.message);
        }
      }

      songIndex++;
    }

    const hasErrors = Object.keys(errors).length > 0;

    // Note: Detailed validation errors are shown in the dialog instead of toast notifications

    setValidationProgress(prev => ({
      ...prev,
      isValidating: false,
      validationErrors: errors,
      hasValidationErrors: hasErrors,
    }));

    return !hasErrors;
  }, [songShares]);

  // Submit function for creating songs and recordings
  const submitAllSongs = React.useCallback(async () => {
    try {
      // Validate all songs first
      const isValid = await validateAllSongs();
      if (!isValid) {
        toast.error(t("fix-validation-errors-before-submit") ?? "Please go back and fix validation errors before submitting");
        return;
      }

      if (onSubmit) {
        await onSubmit(songShares);
      }

      // Start uploading phase
      setValidationProgress(prev => ({
        ...prev,
        isUploading: true,
        uploadedSongs: 0,
      }));

      const songDataArray = Object.values(songShares);
      let uploadedCount = 0;

      // Process each song
      for (const songData of songDataArray) {
        const track = selectedTracks.find(t => t.id === songData.songId);
        if (!track)
          continue;

        const createSongData = {
          artistaccountcode: selectedAccount,
          title: songData.songTitle,
          durationmins: songData.durationMins,
          durationsecs: songData.durationSecs,
          isremix: track.isRemix ?? false,
          containssamples: track.containsSamples ?? false,
          ispublicdomain: false,
          alternativetitles: songData.alternativeTitles.map(title => ({ title })),
          performingartistnames: songData.performingArtists.map(name => ({ name })),
          writersplits: songData.writers.map(writer => ({
            writercode: writer.writercode,
            writershare: writer.writershare,
            writerdesignationcode: String(writer.writerdesignationcode),
            righttocollect: writer.righttocollect,
          })),
          recordings: [{
            artistaccountcode: selectedAccount,
            title: songData.songTitle,
            performingartist: songData.performingArtists[0] || "",
            // Handle null/undefined/empty values properly - only include if they have valid, non-empty values
            ...(((track.external_ids?.isrc) != null) && typeof track.external_ids.isrc === "string" && track.external_ids.isrc.trim() !== "" ? { isrc: track.external_ids.isrc } : {}),
            ...(track.id && typeof track.id === "string" && track.id.trim() !== "" ? { spotifyidentifier: track.id } : {}),
            ...(track.popularity != null ? { spotifypopularity: track.popularity } : {}),
            ...(track.explicit != null ? { explicit: track.explicit } : {}),
            ...(track.track_number != null ? { tracknumber: track.track_number } : {}),
            ...(track.disc_number != null ? { discnumber: track.disc_number } : {}),
            ...(((albumData?.label) != null) && typeof albumData.label === "string" && albumData.label.trim() !== "" ? { recordlabel: albumData.label } : {}),
            ...(((track.album?.release_date) != null) && typeof track.album.release_date === "string" && track.album.release_date.trim() !== "" ? { releasedate: track.album.release_date } : {}),
          }],
        };

        // console.log(`Creating song "${songData.songTitle}" with metadata:`, {
        //   songTitle: songData.songTitle,
        //   isrc: track.external_ids?.isrc,
        //   isrcStatus: track.external_ids?.isrc != null && track.external_ids.isrc !== "" ? "✅ ISRC FOUND" : "❌ NO ISRC",
        //   spotifyId: track.id,
        //   popularity: track.popularity,
        //   explicit: track.explicit,
        //   trackNumber: track.track_number,
        //   discNumber: track.disc_number,
        //   recordLabel: albumData?.label,
        //   releaseDate: track.album?.release_date,
        //   hasExternalIds: !!track.external_ids,
        //   allExternalIds: track.external_ids,
        // });

        // console.log(`🎵 Final createSongData payload for "${songData.songTitle}":`, {
        //   title: songData.songTitle,
        //   recordings: createSongData.recordings,
        //   isrcInRecordings: createSongData.recordings?.[0]?.isrc,
        //   recordLabelInRecordings: createSongData.recordings?.[0]?.recordlabel,
        //   releaseDateInRecordings: createSongData.recordings?.[0]?.releasedate,
        // });

        await createSong(createSongData);
        uploadedCount++;

        // Update progress
        setValidationProgress(prev => ({
          ...prev,
          uploadedSongs: uploadedCount,
        }));
      }

      // Show success phase
      setValidationProgress(prev => ({
        ...prev,
        isUploading: false,
        isSuccess: true,
      }));

      toast.success(t("songs-created-successfully", { count: uploadedCount }));
      // { IN LOCIZE
      //   "songs-created-successfully_one": "{{count}} song created successfully!",
      //   "songs-created-successfully_other": "{{count}} songs created successfully!"
      // }
    }
    catch (error) {
      console.error("Error creating songs:", error);
      setValidationProgress(prev => ({
        ...prev,
        isUploading: false,
        isSuccess: false,
      }));
      toast.error(t("error-creating-songs"));
    }
  }, [songShares, selectedTracks, selectedAccount, createSong, onSubmit, validateAllSongs, albumData?.label, t]);

  // Expose submit function for parent component
  React.useEffect(() => {
    if (onValidationComplete) {
      onValidationComplete(allSongsValid, submitAllSongs);
    }
  }, [allSongsValid, submitAllSongs, onValidationComplete]);

  const songDataArray = Object.values(songShares);

  if (songDataArray.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className={`text-sm ${isTunecore ? "text-gray-300" : "text-gray-500"}`}>
          {t("no-songs-found")}
        </Text>
      </div>
    );
  }

  return (
    <>
      {/* Songs Summary List */}
      <ScrollArea className={`h-[800px] ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-sentric-light-grey p-4 rounded-lg"}`}>

        {/* Validation warnings summary */}
        {(() => {
          const songsWithTitleWarnings = songDataArray.filter(song => getTitleValidationInfo(song.songTitle).hasInvalidChars);
          const songsWithAltTitleWarnings = songDataArray.filter(song =>
            song.alternativeTitles.some(title => title.length > 0 && !validateAlternativeTitle(title).isValid),
          );
          const totalWarnings = songsWithTitleWarnings.length + songsWithAltTitleWarnings.length;

          if (totalWarnings > 0) {
            return (
              <div className={`mt-3 p-4 mb-4 rounded-md ${isTunecore ? "bg-yellow-600/20 border border-yellow-600" : "bg-sentric-orange border"}`}>
                <Text as="subtitle" className={isTunecore ? "text-yellow-400" : ""}>
                  {t("character-validation-warnings-detected")}
                </Text>
                <Text as="p" className={isTunecore ? "text-yellow-300" : ""}>
                  {songsWithTitleWarnings.length > 0 && (
                    t("songs-with-invalid-title-characters", { count: songsWithTitleWarnings.length })
                    ?? `${songsWithTitleWarnings.length} song${songsWithTitleWarnings.length > 1 ? "s" : ""} have invalid title characters. `
                  )}

                  {songsWithAltTitleWarnings.length > 0 && (
                    t("songs-with-invalid-alternative-title-characters", { count: songsWithAltTitleWarnings.length })
                    ?? `${songsWithAltTitleWarnings.length} song${songsWithAltTitleWarnings.length > 1 ? "s" : ""} have invalid alternative title characters. `
                  )}
                </Text>
                <Text as="p" className={`mt-2 ${isTunecore ? "text-yellow-300" : ""}`}>
                  {t("auto-corrected-submission-warning") ?? "These will be auto-corrected during submission, but you may want to review them."}
                </Text>
              </div>
            );
          }
          return null;
        })()}

        {!allSongsValid && (
          <Text as="p" className={`mt-2 ${isTunecore ? "text-red-400" : "text-red-600"}`}>
            {t("fix-validation-issues") ?? "Please go back to the previous step to fix the validation issues."}
          </Text>
        )}

        <Accordion type="multiple" className="space-y-2">
          {songDataArray.map((songData, index) => {
            const isValid = songData.writers.length > 0
              && Math.abs(songData.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) <= 0.01
              && songData.writers.some(w => w.righttocollect);

            // Check for title validation issues
            const titleValidation = getTitleValidationInfo(songData.songTitle);
            const invalidAlternativeTitles = songData.alternativeTitles
              .map((title, idx) => ({ title, index: idx, ...validateAlternativeTitle(title) }))
              .filter(item => !item.isValid && item.title.length > 0);

            const hasValidationWarnings = titleValidation.hasInvalidChars || invalidAlternativeTitles.length > 0;

            return (
              <AccordionItem key={songData.songId} value={songData.songId} className={`rounded-lg shadow-sm ${isTunecore ? "bg-tunecore-grey-element border border-tunecore-mid-grey" : "bg-white border"}`}>
                <AccordionTrigger className={`px-6 rounded-lg py-4 hover:no-underline transition-colors ${isTunecore ? "hover:bg-tunecore-light-grey" : "hover:bg-sentric-mid-grey"}`}>
                  <div className="flex items-center w-full gap-4">
                    <AccordionItemNumber
                      number={index + 1}
                      hasError={!isValid || hasValidationWarnings}
                    />
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <Text as="subtitle">
                          {songData.songTitle}
                        </Text>
                        <Button
                          className="self-center p-1 group"
                          variant="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSong(songData.songId);
                          }}
                          icon={<IconCreate className={`w-5 h-5 self-center ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black group-hover:stroke-sentric-orange"}`} />}
                        />
                        {/* {isValid ? (
                          <IconTick className="w-5 h-5 text-green-500" />
                        ) : (
                          <div className="flex items-center justify-center w-5 h-5 bg-red-500 rounded-full">
                            <span className="text-xs font-bold text-white">!</span>
                          </div>
                        )}
                        {hasValidationWarnings && (
                          <div className="flex items-center justify-center w-5 h-5 bg-yellow-500 rounded-full">
                            <span className="text-xs font-bold text-white">⚠</span>
                          </div>
                        )} */}
                      </div>

                      {/* Title validation warning */}
                      {titleValidation.hasInvalidChars && (
                        <div className={`mb-2 p-2 rounded-md border ${isTunecore ? "bg-yellow-600/20 border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                          <Text as="p" className={isTunecore ? "text-yellow-400" : "text-yellow-800"}>
                            <strong>
                              {t("title-warning") ?? "Title Warning"}
                              :
                            </strong>
                            {" "}
                            {t("contains-invalid-characters-suggested", { suggestedTitle: titleValidation.suggestedTitle })
                              ?? `Contains invalid characters. Suggested: "${titleValidation.suggestedTitle}"`}
                          </Text>
                        </div>
                      )}

                      {/* Alternative titles validation warnings */}
                      {invalidAlternativeTitles.length > 0 && (
                        <div className={`mb-2 p-2 rounded-md border ${isTunecore ? "bg-yellow-600/20 border-yellow-600" : "bg-yellow-50 border-yellow-200"}`}>
                          <Text as="p" className={isTunecore ? "text-yellow-400" : "text-yellow-800"}>
                            <strong>
                              {t("alternative-title-warnings") ?? "Alternative Title Warnings"}
                              :
                            </strong>
                            {" "}
                            {invalidAlternativeTitles
                              .map(item => (
                                t("contains-invalid-characters-suggested", { suggestedTitle: item.suggestedTitle })
                                ?? `Contains invalid characters. Suggested: "${item.suggestedTitle}"`
                              ))
                              .join(" ")}
                          </Text>
                        </div>
                      )}

                      <div className="flex items-center gap-6 ">
                        <span>
                          {t("duration") ?? "Duration"}
                          {": "}
                          {songData.durationMins}
                          :
                          {songData.durationSecs.toString().padStart(2, "0")}
                        </span>
                        <span>
                          {t("performing-artists") ?? "Performing Artists"}
                          {": "}
                          {" "}
                          {songData.performingArtists.join(", ")}
                        </span>
                      </div>
                    </div>

                  </div>
                </AccordionTrigger>

                <AccordionContent className="px-6 pt-2 pb-4">
                  <div className="space-y-2">
                    {/* Alternative Titles */}
                    {songData.alternativeTitles.length > 0 && (
                      <div>
                        <Text as="p" className={`font-medium mb-1 ${isTunecore ? "text-gray-300" : ""}`}>
                          {t("alternative-titles") ?? "Alternative Titles"}
                          :
                        </Text>
                        <Text as="p" className={isTunecore ? "text-gray-400" : ""}>
                          {songData.alternativeTitles.join(", ")}
                        </Text>
                      </div>
                    )}

                    {/* Writers Section */}
                    <div>
                      <Text as="p" className={`font-medium mb-2 ${isTunecore ? "text-gray-300" : ""}`}>
                        {/* Writers & Shares: */}
                        {t("writers-and-shares") ?? "Writers & Shares"}
                      </Text>
                      <div className="space-y-2">
                        {songData.writers.map((writer, _writerIndex) => {
                          const designation = DESIGNATION_OPTIONS.find(opt => opt.value === writer.writerdesignationcode)?.label ?? t("unknown") ?? "Unknown";
                          return (
                            <div key={writer.writercode} className={`flex items-center justify-between p-3 rounded ${isTunecore ? "bg-tunecore-light-grey" : "bg-sentric-light-grey"}`}>
                              <div className="flex-1">
                                <Text as="p" className={`font-medium ${isTunecore ? "text-white" : ""}`}>
                                  {writer.writername}
                                </Text>
                                <Text as="small">
                                  {writer.writercode}
                                  {" "}
                                  /
                                  {" "}
                                  {designation}
                                </Text>
                              </div>
                              <div className="flex items-center gap-3">
                                {writer.righttocollect && (
                                  <Text as="small" className={`text-xs px-2 py-1 rounded ${isTunecore ? "bg-green-600 text-green-200" : "bg-green-100 text-green-800"}`}>
                                    {/* Right To Collect */}
                                    {t("right-to-collect") ?? "Right To Collect"}
                                  </Text>
                                )}
                                <Text as="p" className={`font-medium ${isTunecore ? "text-white" : ""}`}>
                                  {writer.writershare.toFixed(2)}
                                  %
                                </Text>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-2 text-right">
                        <Text
                          as="p"
                          className={` font-medium ${
                            Math.abs(songData.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) <= 0.01
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          Total:
                          {" "}
                          {songData.writers.reduce((sum, w) => sum + w.writershare, 0).toFixed(2)}
                          %
                        </Text>
                      </div>
                    </div>

                    {/* Validation Status */}
                    {!isValid && (
                      <div className={`rounded p-3 border ${isTunecore ? "bg-red-900/20 border-red-600" : "bg-red-50 border-red-200"}`}>
                        <Text as="p" className={`font-medium ${isTunecore ? "text-red-400" : "text-red-800"}`}>
                          {t("validation-issues-detected") ?? "This song has validation issues:"}
                        </Text>
                        <ul className={`mt-1 space-y-1 ${isTunecore ? "text-red-300" : "text-red-700"}`}>
                          {songData.writers.length === 0 && (
                            <li>
                              {" "}
                              {t("no-writers-assigned") ?? "No writers assigned"}
                            </li>
                          )}
                          {Math.abs(songData.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) > 0.01 && (
                            <li>
                              {" "}
                              {t("writer-shares-total-100") ?? "Writer shares must total 100%"}
                            </li>
                          )}
                          {!songData.writers.some(w => w.righttocollect) && (
                            <li>
                              {" "}
                              {t("no-writer-right-to-collect") ?? "At least one writer must have 'Right To Collect' enabled"}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </ScrollArea>

      {/* Overall Status */}
      <div className={`rounded-lg p-4 text-center ${isTunecore ? "bg-tunecore-grey-element" : "bg-sentric-light-grey"}`}>
        <Text as="p" className={`font-semibold ${allSongsValid ? "text-green-600" : "text-red-600"}`}>
          {allSongsValid
            ? t("all-songs-ready", { count: songDataArray.length }) ?? `All ${songDataArray.length} songs are ready for submission!`
            : (() => {
                const invalidSongsCount = songDataArray.filter((song) => {
                  const isValid = song.writers.length > 0
                    && Math.abs(song.writers.reduce((sum, w) => sum + w.writershare, 0) - 100) <= 0.01
                    && song.writers.some(w => w.righttocollect);
                  return !isValid;
                }).length;
                return t("songs-need-attention", { count: invalidSongsCount }) ?? `⚠️ ${invalidSongsCount} song(s) need attention before submission`;
              })()}
        </Text>
      </div>

      {/* Validation/Upload/Success Modal */}
      <Dialog
        open={validationProgress.isValidating || validationProgress.isUploading || validationProgress.isSuccess || validationProgress.hasValidationErrors}
        onOpenChange={(open) => {
          if (!open) {
            setValidationProgress(prev => ({
              ...prev,
              isValidating: false,
              hasValidationErrors: false,
              isSuccess: false,
            }));
          }
        }}
      >
        <DialogContent className={`max-w-2xl max-h-[80vh] ${isTunecore ? "bg-[#282828] text-white border-gray-600" : ""}`}>
          <DialogHeader>
            <DialogTitle>
              {validationProgress.isValidating && (t("validating-songs") ?? "Validating Songs")}
              {validationProgress.isUploading && (t("creating-songs") ?? "Creating Songs")}
              {validationProgress.isSuccess && (t("songs-created-successfully-final") ?? "Songs Created Successfully")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Validation Phase */}
            {(validationProgress.isValidating || validationProgress.hasValidationErrors) && (
              <>
                {validationProgress.isValidating && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                      <Text as="p">
                        {t("validating-song") ?? "Validating song"}
                        {" "}
                        {validationProgress.currentSongIndex + 1}
                        {" "}
                        {t("of") ?? "of"}
                        {" "}
                        {validationProgress.totalSongs}
                      </Text>
                    </div>

                    <div className={`w-full rounded-full h-2 ${isTunecore ? "bg-gray-700" : "bg-gray-200"}`}>
                      <div
                        className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
                        style={{
                          width: `${validationProgress.totalSongs > 0 ? (validationProgress.currentSongIndex / validationProgress.totalSongs) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </>
                )}

                {/* { IN LOCIZE
                    "validation-errors-detected_one": "⚠️ {{count}} song has validation errors",
                    "validation-errors-detected_other": "⚠️ {{count}} songs have validation errors",
                    "close-dialog-fix-errors": "Please close this dialog and go back to fix the errors before submitting.",
                    "close-go-back": "Close & Go Back"
                  } */}
                {validationProgress.hasValidationErrors && (
                  <div className={`rounded-lg p-4 border ${isTunecore ? "bg-red-900/20 border-red-600" : "bg-red-50 border-red-200"}`}>
                    <Text as="p" className={`font-medium mb-3 ${isTunecore ? "text-red-400" : "text-red-800"}`}>
                      {t("dialog-validation-errors-detected", {
                        count: Object.keys(validationProgress.validationErrors).length,
                      }) ?? `⚠️ ${Object.keys(validationProgress.validationErrors).length} song(s) have validation errors`}
                    </Text>

                    {/* List of validation errors */}
                    <ScrollArea className="mb-4 h-60">
                      <div className="pr-3 space-y-3">
                        {Object.entries(validationProgress.validationErrors).map(([songId, songErrors]) => {
                          const songData = songShares[songId];
                          const songTitle = songData?.songTitle ?? "Unknown Song";
                          return (
                            <div key={songId} className={`border rounded p-3 ${isTunecore ? "bg-gray-700 border-red-500" : "bg-white border-red-300"}`}>
                              <Text as="p" className={`font-medium mb-1 ${isTunecore ? "text-red-400" : "text-red-800"}`}>
                                "
                                {songTitle}
                                "
                              </Text>
                              <ul className={`text-sm space-y-1 ${isTunecore ? "text-red-300" : "text-red-700"}`}>
                                {songErrors.map(error => (
                                  <li key={`${songId}-${error}`} className="flex items-start gap-2">
                                    <span className={`mt-0.5 ${isTunecore ? "text-red-400" : "text-red-500"}`}>•</span>
                                    <span>{error}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>

                    <Text as="p" className={`text-sm mb-3 ${isTunecore ? "text-red-300" : "text-red-700"}`}>
                      {t("close-dialog-fix-errors") ?? "Please close this dialog and go back to fix the errors before submitting."}
                    </Text>
                    <div className="text-center">
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => setValidationProgress(prev => ({ ...prev, hasValidationErrors: false }))}
                        label={t("close-go-back") ?? "Close & Go Back"}
                        className="bg-red-600 hover:bg-red-700"
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Upload Phase {
              "creating-song-progress": "Creating song {{current}} of {{total}}",
              "creating-songs-in-account": "Creating songs in your account..."
            } */}
            {validationProgress.isUploading && (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 border-b-2 border-green-600 rounded-full animate-spin"></div>
                  <Text as="p">
                    {t("creating-song-progress", {
                      current: validationProgress.uploadedSongs + 1,
                      total: validationProgress.totalSongs,
                    }) ?? `Creating song ${validationProgress.uploadedSongs + 1} of ${validationProgress.totalSongs}`}
                  </Text>
                </div>

                <div className={`w-full rounded-full h-2 ${isTunecore ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div
                    className="h-2 transition-all duration-300 bg-green-600 rounded-full"
                    style={{
                      width: `${validationProgress.totalSongs > 0 ? (validationProgress.uploadedSongs / validationProgress.totalSongs) * 100 : 0}%`,
                    }}
                  />
                </div>

                <div className={`rounded-lg p-3 border ${isTunecore ? "bg-blue-900/20 border-blue-600" : "bg-blue-50 border-blue-200"}`}>
                  <Text as="p" className={`${isTunecore ? "text-blue-400" : "text-blue-800"}`}>
                    {t("creating-songs-in-account") ?? "Creating songs in your account..."}
                  </Text>
                </div>
              </>
            )}

            {/* Success Phase  {
                "all-songs-created-successfully": "All {{count}} songs created successfully!",
                "songs-added-to-catalog": "Your songs have been successfully added to your catalog! You can now view and manage them.",
                "back-to-songs": "Back to Songs"
              } */}
            {validationProgress.isSuccess && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-5 h-5 bg-green-600 rounded-full">
                    <IconTick className="w-3 h-3 text-white" />
                  </div>
                  <Text as="p" className={`text-green-800 font-medium ${isTunecore ? "text-green-400" : ""}`}>
                    {t("all-songs-created-successfully", { count: validationProgress.totalSongs })
                      ?? `All ${validationProgress.totalSongs} songs created successfully!`}
                  </Text>
                </div>

                <div className={`w-full rounded-full h-2 ${isTunecore ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div className="w-full h-2 bg-green-600 rounded-full"></div>
                </div>

                <div className={`rounded-lg p-3 border ${isTunecore ? "bg-green-900/20 border-green-600" : "bg-green-50 border-green-200"}`}>
                  <Text as="p" className={`mb-3 ${isTunecore ? "text-green-400" : "text-green-800"}`}>
                    {t("songs-added-to-catalog") ?? "Your songs have been successfully added to your catalog! You can now view and manage them."}
                  </Text>
                  <Button
                    onClick={() => {
                      window.location.href = "/songs";
                    }}
                    variant="primary"
                    label={t("back-to-songs") ?? "Back to Songs"}
                  />
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>

  );
}
